import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { headerSty, contentSty } from "../table-styles";
import {
  formatDecimalNumber,
  formatTextType,
  formatTime,
  getUrlPathId,
  limitToSeven,
} from "../../utils/url-id";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { apiGetMedicIdTimesheet } from "../../api/medic";
import { useNotificationService } from "../notification";
import { MedicTimesheetInfo } from "../../interface/medic";
import {
  apiGetFacilitiesJobs,
  apiGetFacilitiesJobsAnalystics,
} from "../../api/facility";
import {
  FacilityJobsDetailsProps,
  FacilityJobsProps,
} from "../../interface/facility";
import { Pagination } from "../Pagination";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { JobDetailsModal } from "./modal-contents";
import JobStats from "./jobStats";
import { FilterJobsInputs } from "./FiltterInputs";

export default function JobFacility({
  stats,
  jobs_list,
  loading,
  pagination_state,
  setFilterData,
  setPagination_state,
}: FacilityJobsDetailsProps) {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: "#93A5D8", fontWeight: 400 };
  const contentStyles = { ...contentSty };
  const location = useLocation();

  const navigate = useNavigate();
  const [compare, setCompare] = React.useState(false);

  return (
    <>
      <JobStats stats={stats} />
      <Box
        sx={{
          background: "#FAFBFC",
          m: 2,
          width: "98%",
          py: 3,
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ mb: 1 }}
        >
          <Typography
            variant="subtitle2"
            sx={{ ...headerSty, textAlign: "left", px: 3 }}
          >
            Facility Employees
          </Typography>

          <Button onClick={() => setCompare(!compare)}>
            {compare ? <FilterAltOffIcon /> : <FilterAltIcon />}
          </Button>
        </Stack>
        {compare && (
          <FilterJobsInputs loading={loading} setFilterData={setFilterData} />
        )}
        <TableContainer
          component={Paper}
          sx={{
            px: 2,
            py: 3,
            width: "100%",
            mx: "auto",
            minHeight: 300,
            background: "#FAFBFC",
            borderRadius: 1,
            boxShadow: "none",
          }}
        >
          <Table
            sx={{ width: "98%", mx: "auto" }}
            size="small"
            aria-label="a dense table"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headStyles }}>ID</TableCell>
                <TableCell sx={{ ...headStyles }}>Job Title</TableCell>
                <TableCell sx={{ ...headStyles }}>Employment Type</TableCell>
                <TableCell sx={{ ...headStyles }}>Job Type</TableCell>
                <TableCell sx={{ ...headStyles }}>Status </TableCell>
                <TableCell sx={{ ...headStyles }}>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                jobs_list.map((row: FacilityJobsProps) => (
                  <TableRow
                    key={row._id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    onClick={() => navigate(location.pathname + "/" + row._id)}
                  >
                    <TableCell
                      sx={{
                        ...contentStyles,
                        textAlign: "left",
                        padding: "12px",
                      }}
                      component="th"
                      scope="row"
                    >
                      {limitToSeven(row._id) ?? "-"}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {row?.job_title}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {formatTextType(row?.employment_type)}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {formatTextType(row?.job_type)}
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      <JobEmpStatus status={row.status} />
                    </TableCell>
                    <TableCell sx={{ ...contentStyles, padding: "12px" }}>
                      {formatTextType(row?.work_location)}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {jobs_list?.length === 0 && !loading && (
            <Box
              sx={{
                height: 200,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
              >
                No Job found
              </Typography>
            </Box>
          )}
          {loading && (
            <Box sx={{ width: "96%", mx: "auto" }}>
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
              <Skeleton
                variant="rounded"
                width={"100%"}
                height={60}
                sx={{ my: 2 }}
              />
            </Box>
          )}
        </TableContainer>
      
     
        <Pagination
          paginationState={pagination_state}
          setPaginationState={setPagination_state}
        />
      </Box>
    </>
  );
}

export const JobEmpStatus: React.FC<{
  status: "pending" | "closed" | "active" | string;
}> = ({ status }) => {
  const theme = useTheme();

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "pending":
        return {
          color: theme.palette.warning.main,
          backgroundColor: "#ffac336b",
        };
      case "closed":
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
      case "active":
        return {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.lightBlue.main,
        };
      default:
        return {
          color: theme.palette.error.main,
          backgroundColor: "#f6685e6b",
        };
    }
  };

  const statusStyles = getStatusStyles(status);

  return (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ width: 100, mx: "auto" }}
    >
      <Box
        sx={{
          marginX: 1,
          paddingY: 1,
          paddingX: 3,
          borderRadius: "3px",
          fontSize: 12,
          fontFamily: "Lexend",
          textTransform: "uppercase",
          ...statusStyles,
        }}
      >
        {status}
      </Box>
    </Stack>
  );
};
